import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  Button,
  Container,
  Typography,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useTranslation } from "react-i18next";
import { useAuth } from "../contexts/AuthContext";
import { useLocale } from "../contexts/LocaleContext";
import { createCheckoutSession } from "../utils/firebase";
import { getPlanById, PAID_PLAN_IDS } from "../config/planConfig";

export default function CheckoutPage() {
  const { user, loading: authLoading } = useAuth();
  const { t } = useTranslation();
  const { basePath, localizePath } = useLocale();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const planParam = searchParams.get("plan");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const planId = planParam && PAID_PLAN_IDS.includes(planParam) ? planParam : null;
  const plan = planId ? getPlanById(planId) : null;

  useEffect(() => {
    if (!authLoading && !user) {
      const loginPath = localizePath("/login");
      const redirect = `${basePath || ""}/checkout${planId ? `?plan=${planId}` : ""}`.replace(/\/+/g, "/");
      navigate(`${loginPath}?redirect=${encodeURIComponent(redirect)}`, { replace: true });
    }
  }, [user, authLoading, navigate, planId, basePath, localizePath]);

  useEffect(() => {
    if (!authLoading && user && !planId) {
      navigate(localizePath("/billing"), { replace: true });
    }
  }, [authLoading, user, planId, navigate, localizePath]);

  const handleSubscribe = async () => {
    if (!user || !planId) return;
    setError(null);
    setSubmitting(true);
    try {
      const appUrl = window.location.origin;
      const result = await createCheckoutSession({
        planId,
        returnUrl: `${appUrl}${basePath || ""}/checkout/success`,
        cancelUrl: `${appUrl}${basePath || ""}/checkout/cancel`,
      });
      if (result.data?.approvalUrl) {
        window.location.href = result.data.approvalUrl;
      } else {
        setError(t("checkout.errorCreateSession"));
      }
    } catch (err) {
      setError(err.message || t("checkout.errorStartCheckout"));
    } finally {
      setSubmitting(false);
    }
  };

  if (authLoading || !user || !plan) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="40vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ py: 6 }}>
      <Typography
        variant="h4"
        sx={{
          fontFamily: '"Cal Sans", "Inter", sans-serif',
          fontWeight: 600,
          mb: 3,
          textAlign: "center",
        }}
      >
        {t("checkout.title")}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card
        variant="outlined"
        sx={{
          mb: 4,
          borderRadius: 2,
          borderColor: "primary.main",
          borderWidth: 2,
        }}
      >
        <CardContent sx={{ py: 3 }}>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            {plan.name}
          </Typography>
          <Box mb={2}>
            <Typography component="span" variant="h4" fontWeight={600}>
              {plan.priceDisplay}
            </Typography>
            <Typography component="span" color="text.secondary" variant="body2" sx={{ ml: 0.5 }}>
              /{plan.period.replace("per ", "")}
            </Typography>
          </Box>
          <List dense disablePadding>
            {plan.benefits.map((benefit, i) => (
              <ListItem key={i} disableGutters sx={{ py: 0.25 }}>
                <ListItemIcon sx={{ minWidth: 28 }}>
                  <CheckCircleIcon sx={{ fontSize: 18, color: "success.main" }} />
                </ListItemIcon>
                <Typography variant="body2" color="text.secondary">
                  {benefit}
                </Typography>
              </ListItem>
            ))}
          </List>
        </CardContent>
      </Card>

      <Button
        variant="contained"
        size="large"
        fullWidth
        onClick={handleSubscribe}
        disabled={submitting}
        startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : null}
        sx={{ py: 1.5 }}
      >
        {submitting ? t("checkout.redirecting") : t("checkout.continueToPayment")}
      </Button>

      <Button
        variant="text"
        size="small"
        fullWidth
        onClick={() => navigate(localizePath("/billing"))}
        sx={{ mt: 2 }}
      >
        {t("checkout.chooseDifferentPlan")}
      </Button>
    </Container>
  );
}
